import { enUS, es, it, pt, ptBR } from "date-fns/locale";

export type AppLanguage = "pt-BR" | "pt-PT" | "en" | "es" | "it";
export type AppCurrency =
  | "BRL"
  | "ARS"
  | "BOB"
  | "CLP"
  | "COP"
  | "CRC"
  | "CUP"
  | "DOP"
  | "GTQ"
  | "HNL"
  | "HTG"
  | "MXN"
  | "NIO"
  | "PAB"
  | "PEN"
  | "PYG"
  | "UYU"
  | "USD"
  | "VES"
  | "ALL"
  | "BAM"
  | "EUR"
  | "GBP"
  | "GEL"
  | "CHF"
  | "SEK"
  | "NOK"
  | "DKK"
  | "PLN"
  | "CZK"
  | "HUF"
  | "MDL"
  | "MKD"
  | "RON"
  | "RSD"
  | "BGN"
  | "ISK"
  | "TRY"
  | "UAH";

export type CurrencyRegion = "latin-america" | "europe";
export type AppTimezone =
  | "America/Sao_Paulo"
  | "America/New_York"
  | "Europe/Lisbon"
  | "Europe/Madrid"
  | "Europe/Rome";

export const DEFAULT_LANGUAGE: AppLanguage = "pt-BR";
export const DEFAULT_CURRENCY: AppCurrency = "BRL";
export const DEFAULT_TIMEZONE: AppTimezone = "America/Sao_Paulo";
export const SUPPORTED_CURRENCIES = ["BRL", "USD", "EUR", "GBP"] as const;
export const TIMEZONE_OPTIONS: Array<{ value: AppTimezone; label: string }> = [
  { value: "America/Sao_Paulo", label: "São Paulo (América)" },
  { value: "America/New_York", label: "Nova York (América)" },
  { value: "Europe/Lisbon", label: "Lisboa (Europa)" },
  { value: "Europe/Madrid", label: "Madri (Europa)" },
  { value: "Europe/Rome", label: "Roma (Europa)" },
];
export const LANGUAGE_STORAGE_KEY = "fluxus-language";
export const CURRENCY_STORAGE_KEY = "fluxus-currency";

export const LANGUAGE_LABELS: Record<AppLanguage, string> = {
  "pt-BR": "Português",
  "pt-PT": "Português (Portugal)",
  en: "English",
  es: "Español",
  it: "Italiano",
};

export const LANGUAGE_SHORT_LABELS: Record<AppLanguage, string> = {
  "pt-BR": "PT",
  "pt-PT": "PT",
  en: "EN",
  es: "ES",
  it: "IT",
};

export const INTL_LOCALES: Record<AppLanguage, string> = {
  "pt-BR": "pt-BR",
  "pt-PT": "pt-PT",
  en: "en-US",
  es: "es-ES",
  it: "it-IT",
};

export const DATE_FNS_LOCALES = {
  "pt-BR": ptBR,
  "pt-PT": pt,
  en: enUS,
  es,
  it,
} as const;

export const CURRENCY_REGION_LABELS: Record<CurrencyRegion, string> = {
  "latin-america": "Am\u00e9rica Latina",
  europe: "Europa",
};

export const CURRENCY_OPTIONS: Array<{
  code: AppCurrency;
  name: string;
  region: CurrencyRegion;
}> = [
  { code: "BRL", name: "Real brasileiro", region: "latin-america" },
  { code: "ARS", name: "Peso argentino", region: "latin-america" },
  { code: "BOB", name: "Boliviano", region: "latin-america" },
  { code: "CLP", name: "Peso chileno", region: "latin-america" },
  { code: "COP", name: "Peso colombiano", region: "latin-america" },
  { code: "CRC", name: "Col\u00f3n costarriquenho", region: "latin-america" },
  { code: "CUP", name: "Peso cubano", region: "latin-america" },
  { code: "DOP", name: "Peso dominicano", region: "latin-america" },
  { code: "GTQ", name: "Quetzal guatemalteco", region: "latin-america" },
  { code: "HNL", name: "Lempira hondurenha", region: "latin-america" },
  { code: "HTG", name: "Gourde haitiano", region: "latin-america" },
  { code: "MXN", name: "Peso mexicano", region: "latin-america" },
  { code: "NIO", name: "C\u00f3rdoba nicaraguense", region: "latin-america" },
  { code: "PAB", name: "Balboa panamenho", region: "latin-america" },
  { code: "PEN", name: "Sol peruano", region: "latin-america" },
  { code: "PYG", name: "Guarani paraguaio", region: "latin-america" },
  { code: "UYU", name: "Peso uruguaio", region: "latin-america" },
  { code: "USD", name: "D\u00f3lar americano", region: "latin-america" },
  { code: "VES", name: "Bol\u00edvar venezuelano", region: "latin-america" },
  { code: "ALL", name: "Lek alban\u00eas", region: "europe" },
  { code: "BAM", name: "Marco convers\u00edvel", region: "europe" },
  { code: "EUR", name: "Euro", region: "europe" },
  { code: "GBP", name: "Libra esterlina", region: "europe" },
  { code: "GEL", name: "Lari georgiano", region: "europe" },
  { code: "CHF", name: "Franco su\u00ed\u00e7o", region: "europe" },
  { code: "SEK", name: "Coroa sueca", region: "europe" },
  { code: "NOK", name: "Coroa norueguesa", region: "europe" },
  { code: "DKK", name: "Coroa dinamarquesa", region: "europe" },
  { code: "PLN", name: "Zloty polon\u00eas", region: "europe" },
  { code: "CZK", name: "Coroa tcheca", region: "europe" },
  { code: "HUF", name: "Forint h\u00fangaro", region: "europe" },
  { code: "MDL", name: "Leu moldavo", region: "europe" },
  { code: "MKD", name: "Denar maced\u00f4nio", region: "europe" },
  { code: "RON", name: "Leu romeno", region: "europe" },
  { code: "RSD", name: "Dinar s\u00e9rvio", region: "europe" },
  { code: "BGN", name: "Lev b\u00falgaro", region: "europe" },
  { code: "ISK", name: "Coroa islandesa", region: "europe" },
  { code: "TRY", name: "Lira turca", region: "europe" },
  { code: "UAH", name: "Hryvnia ucraniana", region: "europe" },
];

let currentLanguage: AppLanguage = DEFAULT_LANGUAGE;
let currentCurrency: AppCurrency = DEFAULT_CURRENCY;
let currentTimezone: AppTimezone = DEFAULT_TIMEZONE;

export function isAppLanguage(value: string | null | undefined): value is AppLanguage {
  return value === "pt-BR" || value === "pt-PT" || value === "en" || value === "es" || value === "it";
}

export function isAppCurrency(value: string | null | undefined): value is AppCurrency {
  return CURRENCY_OPTIONS.some((option) => option.code === value);
}

export function getCurrentLanguage(): AppLanguage {
  if (typeof window === "undefined") {
    return currentLanguage;
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isAppLanguage(storedLanguage) ? storedLanguage : currentLanguage;
}

export function setCurrentLanguage(language: AppLanguage): void {
  currentLanguage = language;
}

export function getCurrentCurrency(): AppCurrency {
  return currentCurrency;
}

export function setCurrentCurrency(currency: AppCurrency): void {
  currentCurrency = currency;
}

export function isAppTimezone(value: string | null | undefined): value is AppTimezone {
  return TIMEZONE_OPTIONS.some((option) => option.value === value);
}

export function getCurrentTimezone(): AppTimezone {
  return currentTimezone;
}

export function setCurrentTimezone(timezone: AppTimezone): void {
  currentTimezone = timezone;
}

export function getIntlLocale(language: AppLanguage = getCurrentLanguage()): string {
  return INTL_LOCALES[language];
}

export function getDateFnsLocale(language: AppLanguage = getCurrentLanguage()) {
  return DATE_FNS_LOCALES[language];
}
